import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { ENDPOINTS } from '../../../../common/constants/endpoints.constants';
import { ApiResponse } from '../../../../common/models/api-response.model';
import { ReflectionRunStatusEnum } from './enums/reflection-run-status.enum';
import { ReflectionScopeEnum } from './enums/reflection-scope.enum';
import {
  ReflectionCatalog,
  ReflectionField,
  ReflectionFieldTypeOperators,
  ReflectionLabeledValue,
  ReflectionList,
  ReflectionLookups,
  ReflectionPayload,
  ReflectionReorderPayload,
  ReflectionRun,
} from './reflections.models';

@Injectable({ providedIn: 'root' })
export class ReflectionsService {
  private readonly http = inject(HttpClient);
  private readonly endpoints = ENDPOINTS.Dashboard.Workflow.Reflections;
  private readonly lookupEndpoints = ENDPOINTS.Lookup;
  private catalog$?: Observable<ReflectionCatalog>;
  private lookups$?: Observable<ReflectionLookups>;
  private readonly fields$ = new Map<
    number,
    Observable<{ data: ReflectionField[] }>
  >();

  readonly catalog = signal<ReflectionCatalog | null>(null);
  /**
   * Events, operators and placeholders of the builder. Their labels come localised from the backend in the language
   * of the request; a language switch reloads the app, so one load per session is enough.
   */
  readonly lookups = signal<ReflectionLookups | null>(null);

  /** The catalog is static per session: one request, shared by every caller (also concurrent ones). */
  loadCatalog(): Observable<ReflectionCatalog> {
    this.catalog$ ??= this.http
      .get<{ data: ReflectionCatalog }>(this.endpoints.Catalog)
      .pipe(
        map(response => response.data),
        tap(catalog => this.catalog.set(catalog)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    return this.catalog$;
  }

  /**
   * The lookups are static per session: one load (3 event calls, one per scope, the operators of every field type
   * and the placeholders), shared by every caller (also concurrent ones). A failed load is retried by the next call.
   */
  loadLookups(): Observable<ReflectionLookups> {
    this.lookups$ ??= forkJoin([
      forkJoin(
        Object.values(ReflectionScopeEnum).map(scope =>
          this.lookup<ReflectionLabeledValue>(
            this.lookupEndpoints.ReflectionEvents,
            new HttpParams().set('scope', scope)
          )
        )
      ),
      this.lookup<ReflectionFieldTypeOperators>(
        this.lookupEndpoints.ReflectionOperators
      ),
      this.lookup<ReflectionLabeledValue>(
        this.lookupEndpoints.ReflectionPlaceholders
      ),
    ]).pipe(
      map(([events, operators, placeholders]) => ({
        events: Object.fromEntries(
          Object.values(ReflectionScopeEnum).map((scope, index) => [
            scope,
            events[index],
          ])
        ) as ReflectionLookups['events'],
        operators: Object.fromEntries(
          operators.map(group => [group.field_type, group.operators])
        ),
        placeholders,
      })),
      tap(lookups => this.lookups.set(lookups)),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.lookups$;
  }

  /**
   * The fields reflections can read, per flow template: one request shared by every list of the page (flow and
   * each step) and by concurrent callers. A failed request is made again by the next call. Whatever changes the
   * template's form calls {@link invalidateFields}.
   */
  fields(templateId: number): Observable<{ data: ReflectionField[] }> {
    let fields$ = this.fields$.get(templateId);
    if (!fields$) {
      const url = this.lookupEndpoints.FlowTemplateFields.replace(
        '{id}',
        `${templateId}`
      );
      fields$ = this.http
        .get<{ data: ReflectionField[] }>(url)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
      this.fields$.set(templateId, fields$);
    }
    return fields$;
  }

  /** Drops the cached fields of a template whose form changed; the next {@link fields} call requests them again. */
  invalidateFields(templateId: number): void {
    this.fields$.delete(templateId);
  }

  list(
    templateId: number,
    scope: ReflectionScopeEnum,
    stepTemplateId?: number | null
  ) {
    let params = new HttpParams().append('scope', scope);
    if (stepTemplateId != null) {
      params = params.append('step_template_id', stepTemplateId);
    }
    return this.http.get<{ data: ReflectionList }>(this.base(templateId), {
      params,
    });
  }

  create(templateId: number, body: ReflectionPayload) {
    return this.http.post<{ data: { id: number } }>(
      this.base(templateId),
      body
    );
  }

  update(templateId: number, id: number, body: Partial<ReflectionPayload>) {
    return this.http.put<{ message: string }>(
      `${this.base(templateId)}/${id}`,
      body
    );
  }

  remove(templateId: number, id: number) {
    return this.http.delete(`${this.base(templateId)}/${id}`);
  }

  reorder(templateId: number, body: ReflectionReorderPayload) {
    return this.http.put(`${this.base(templateId)}/reorder`, body);
  }

  runs(
    templateId: number,
    id: number,
    page: number,
    status?: ReflectionRunStatusEnum
  ) {
    let params = new HttpParams().append('page', page);
    if (status) {
      params = params.append('status', status);
    }
    return this.http.get<ApiResponse<ReflectionRun>>(
      `${this.base(templateId)}/${id}/runs`,
      { params }
    );
  }

  /**
   * Names of the users a config stores by id, from the users lookup (one call per id, as the lookup filters by a
   * single id). Users that cannot be resolved are left out.
   */
  userLabels(ids: number[]): Observable<Record<number, string>> {
    if (!ids.length) return of({});
    return forkJoin(
      ids.map(id =>
        this.http
          .get<{
            data: { value: number; label: string }[];
          }>(ENDPOINTS.Lookup.User, {
            params: new HttpParams().set('filters[id]', id),
          })
          .pipe(
            map(response => response.data.find(user => user.value === id)),
            catchError(() => of(undefined))
          )
      )
    ).pipe(
      map(users =>
        Object.fromEntries(
          users.flatMap(user => (user ? [[user.value, user.label]] : []))
        )
      )
    );
  }

  private lookup<T>(url: string, params?: HttpParams): Observable<T[]> {
    return this.http
      .get<{ data: T[] }>(url, { params })
      .pipe(map(response => response.data));
  }

  private base(templateId: number): string {
    return `${this.endpoints.FlowTemplates}/${templateId}/reflections`;
  }
}
